import Grid from '@mui/material/Grid';
import Image from 'next/image';
import { Divider } from '@startengine/design-system.ui.divider';
import { colors } from '@startengine/design-system.theme.main';
import {
  ActionText,
  BadgeGrid,
  BadgeTitleTypography,
  BadgeSubtitleTypography,
  BadgeDisclaimerTypography,
  BadgeContainer,
  BadgeWrapper,
} from './styles';
import { imagePath } from 'utils/helpers';

const getImage = (imageFileName, appearance) => {
  const compact = appearance === 'compactDark';
  const imageWidth = compact ? 48 : 75;
  const imageHeight = compact ? 45 : 70;
  return (
    <Grid width={compact ? '44px' : '54px'} height={compact ? '44px' : '54px'}>
      <div style={{ width: imageWidth, height: imageHeight, overflow: 'visible' }}>
        <Image
          width={imageWidth}
          height={imageHeight}
          priority
          src={`${imagePath}/images/offering/${imageFileName}.png`}
          alt="Criteria Badge"
          data-cy="hero-banner-image"
          quality={100}
        />
      </div>
    </Grid>
  );
};

const Badge = ({ title, description, iconImagePath, appearance }) => {
  return (
    <BadgeContainer container appearance={appearance}>
      <Grid item alignItems="center" pr={1}>
        {getImage(iconImagePath, appearance)}
      </Grid>
      <Grid item container direction="column" justifyContent="center" xs zeroMinWidth>
        <BadgeTitleTypography appearance={appearance}>{title}</BadgeTitleTypography>
        <BadgeSubtitleTypography appearance={appearance}>{description}</BadgeSubtitleTypography>
      </Grid>
    </BadgeContainer>
  );
};

const BadgeDisclaimer = ({ appearance }) => {
  return (
    <Grid item container justifyContent="center">
      <BadgeDisclaimerTypography appearance={appearance}>
        What does this badge mean?{' '}
        <ActionText
          appearance={appearance}
          href="https://help.startengine.com/what-can-startengine-do-to-market-my-raise-ry4s6uRMK"
          target="_blank"
          rel="noopener noreferrer"
        >
          See here
        </ActionText>
      </BadgeDisclaimerTypography>
    </Grid>
  );
};

const BadgeSet = ({ badges, showDisclaimer = false, appearance = 'classic' }) => {
  if (!badges || badges.length === 0) {
    return null;
  }

  // The "What does this badge mean? See here" footer is a tombstone-only
  // explainer (Reg CF / Reg A+ Most Funded + Most Momentum badges).
  // Reg D / 506(c) offerings only carry the "US Investors Only"
  // informational badge, which doesn't need the help-center link
  // (Paige Soffa report Jun 2 2026 on PsiQuantum-IRA-1). Caller decides
  // whether the disclaimer is relevant via `showDisclaimer`.
  const cardPadding = showDisclaimer ? { px: 1, pt: 1, pb: 0 } : { p: 1 };

  return (
    <BadgeWrapper container rowSpacing={1} appearance={appearance}>
      <BadgeGrid
        item
        xs={12}
        container
        my={1}
        data-cy="offering-momentum-card"
        appearance={appearance}
        {...cardPadding}
      >
        {badges.map((badge, index) => (
          <div key={`badge-${index}`}>
            <Grid item xs={12}>
              {badge}
            </Grid>
            {index < badges.length - 1 && (
              <Grid item xs={12}>
                <Divider
                  color={appearance === 'compactDark' ? 'rgba(255,255,255,0.2)' : colors.gray2}
                />
              </Grid>
            )}
          </div>
        ))}
        {showDisclaimer && (
          <>
            <Grid item xs={12} px={1}>
              <Divider
                color={appearance === 'compactDark' ? 'rgba(255,255,255,0.2)' : colors.gray2}
              />
            </Grid>
            <Grid item xs={12} py={1.25}>
              <BadgeDisclaimer appearance={appearance} />
            </Grid>
          </>
        )}
      </BadgeGrid>
    </BadgeWrapper>
  );
};

const CriteriaBadge = ({ offering, appearance = 'classic', showDisclaimer = true }) => {
  const isPopular = offering?.badges?.some((badge) => badge?.name === 'MOST_POPULAR');
  const isMomentum = offering?.badges?.some((badge) => badge?.name === 'MOST_MOMENTUM');
  // const isTrending = offering?.badges?.some((badge) => badge?.name === 'TRENDING');
  const isUSInvestorsOnly = offering?.internationalInvestorsAllowed === false;

  const badges = [];

  if (isUSInvestorsOnly) {
    badges.push(
      <Badge
        title="US Investors Only"
        description="This offering is open only to US investors"
        iconImagePath="criteria-badge-funded"
        appearance={appearance}
        data-cy="us-investors-only-badge"
        key="us-investors-only-badge"
      />,
    );
  }

  if (isMomentum)
    badges.push(
      <Badge
        title="Most Momentum"
        description="Top 15 in amount raised last 72 hours"
        iconImagePath="criteria-badge-momentum"
        appearance={appearance}
      />,
    );
  if (isPopular)
    badges.push(
      <Badge
        title="Most Funded"
        description="Top 15 in amount raised on StartEngine"
        iconImagePath="criteria-badge-funded"
        appearance={appearance}
      />,
    );

  return (
    <BadgeSet
      badges={badges}
      showDisclaimer={showDisclaimer && (isMomentum || isPopular)}
      appearance={appearance}
    />
  );
};

export default CriteriaBadge;
